/**
 * <reviews-grid> — progressive disclosure for the all-reviews page.
 *
 * Stage 1: show a whole number of rows (at least `data-initial` cards) plus a
 *          sliver of the next row, dissolved into the page behind a gradient
 *          with the "read more" button sitting on top of it.
 * Stage 2: after "read more", show `data-per-page` at a time with numbered
 *          pagination.
 *
 * Everything is server-rendered; this only clips the grid and toggles `hidden`
 * on the cards, so every review is present for crawlers and for anyone with JS
 * turned off (who simply sees the full, unclipped list).
 */
if (!customElements.get('reviews-grid')) {
  const MOBILE = '(max-width: 749px)';

  class ReviewsGrid extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('.rv__item'));
      this.viewport = this.querySelector('[data-viewport]');
      this.fade = this.querySelector('[data-fade]');
      this.moreWrap = this.querySelector('[data-more-wrap]');
      this.moreBtn = this.querySelector('[data-more]');
      this.pager = this.querySelector('[data-pager]');
      this.grid = this.querySelector('[data-grid]');

      this.total = this.items.length;
      this.initial = Math.max(1, parseInt(this.dataset.initial, 10) || 9);
      this.initialMobile = Math.max(1, parseInt(this.dataset.initialMobile, 10) || 3);
      this.perPage = Math.max(1, parseInt(this.dataset.perPage, 10) || 12);
      this.peek = this.dataset.peek !== 'false';
      this.peekPercent = Math.min(90, Math.max(10, parseInt(this.dataset.peekPercent, 10) || 50)) / 100;
      this.paginate = this.dataset.paginate !== 'false';
      this.scrollOnChange = this.dataset.scroll !== 'false';

      this.expanded = false;
      this.page = 1;

      if (this.moreBtn) {
        this.moreBtn.addEventListener('click', () => {
          this.expanded = true;
          this.page = 1;
          this.render();
          if (this.moreBtn) this.moreBtn.blur();
        });
      }

      // the cut-off depends on real card heights, so recompute whenever they move
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => this.measure());
        this.ro.observe(this.grid);
      }
      this.onResize = () => this.measure();
      window.addEventListener('resize', this.onResize);

      this.render();

      // webfonts land after first paint and change card heights
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.measure());
      }
    }

    disconnectedCallback() {
      if (this.ro) this.ro.disconnect();
      window.removeEventListener('resize', this.onResize);
    }

    get initialCount() {
      return window.matchMedia(MOBILE).matches ? this.initialMobile : this.initial;
    }

    get pageCount() {
      return Math.max(1, Math.ceil(this.total / this.perPage));
    }

    /** Group the currently visible cards into grid rows by their offset top. */
    rows() {
      const gridTop = this.grid.getBoundingClientRect().top;
      const out = [];
      this.items.forEach((li) => {
        if (li.hidden) return;
        const r = li.getBoundingClientRect();
        if (!r.height) return;
        const top = Math.round(r.top - gridTop);
        const last = out[out.length - 1];
        // 2px of slack absorbs sub-pixel differences between cards in a row
        if (last && Math.abs(last.top - top) <= 2) {
          last.height = Math.max(last.height, Math.round(r.height));
          last.count += 1;
        } else {
          out.push({ top: top, height: Math.round(r.height), count: 1 });
        }
      });
      return out;
    }

    /**
     * Clip to a whole number of rows plus a slice of the next one, so the cut
     * never lands mid-card for the fully-shown reviews.
     */
    measure() {
      if (!this.viewport) return;

      if (this.expanded || !this.peek) {
        this.unclip();
        return;
      }

      const rows = this.rows();
      const want = this.initialCount;
      let shown = 0;
      let fullRows = 0;
      for (let i = 0; i < rows.length; i++) {
        shown += rows[i].count;
        fullRows += 1;
        if (shown >= want) break;
      }

      const peekRow = rows[fullRows];
      if (!peekRow) {
        // everything already fits — nothing to tease
        this.unclip();
        if (this.moreWrap) this.moreWrap.hidden = true;
        return;
      }

      const cut = Math.round(peekRow.top + peekRow.height * this.peekPercent);
      this.viewport.setAttribute('data-clipped', '');
      this.viewport.style.maxHeight = cut + 'px';
      if (this.fade) this.fade.hidden = false;
      if (this.moreWrap) {
        this.moreWrap.hidden = false;
        this.moreWrap.classList.remove('rv__more--static');
      }

      // cards that start below the cut are inert for keyboard and AT users
      // (offsetTop is unreliable here — the offset parent is not the grid)
      const gridTop = this.grid.getBoundingClientRect().top;
      this.items.forEach((li) => {
        const clipped = !li.hidden && Math.round(li.getBoundingClientRect().top - gridTop) >= cut;
        li.classList.toggle('rv__item--clipped', clipped);
        if (clipped) {
          li.setAttribute('inert', '');
        } else {
          li.removeAttribute('inert');
        }
      });
    }

    unclip() {
      this.viewport.removeAttribute('data-clipped');
      this.viewport.style.maxHeight = '';
      if (this.fade) this.fade.hidden = true;
      if (this.moreWrap) this.moreWrap.classList.add('rv__more--static');
      this.items.forEach((li) => {
        li.classList.remove('rv__item--clipped');
        li.removeAttribute('inert');
      });
    }

    render() {
      let from = 0;
      let to = this.total;

      if (this.expanded && this.paginate) {
        from = (this.page - 1) * this.perPage;
        to = from + this.perPage;
      }

      // collapsed keeps every card in the DOM flow — the clip does the hiding,
      // which is what lets half of the next row show through the gradient
      this.items.forEach((li, i) => {
        li.hidden = i < from || i >= to;
      });

      if (this.moreWrap) {
        this.moreWrap.hidden = this.expanded || this.total <= this.initialCount;
      }

      if (this.pager) {
        const needed = this.expanded && this.paginate && this.pageCount > 1;
        this.pager.hidden = !needed;
        if (needed) this.renderPager();
      }

      this.measure();
    }

    renderPager() {
      const pages = this.pageCount;
      const cur = this.page;
      this.pager.textContent = '';

      const btn = (label, page, opts = {}) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'rv__page';
        b.textContent = label;
        if (opts.current) b.setAttribute('aria-current', 'true');
        if (opts.disabled) b.disabled = true;
        if (opts.label) b.setAttribute('aria-label', opts.label);
        if (!opts.disabled && !opts.current) {
          b.addEventListener('click', () => this.goTo(page));
        }
        this.pager.appendChild(b);
      };
      const gap = () => {
        const s = document.createElement('span');
        s.className = 'rv__ellipsis';
        s.textContent = '…';
        this.pager.appendChild(s);
      };

      btn('‹', cur - 1, { disabled: cur === 1, label: 'Previous page' });

      // first, last, and a window around the current page
      const window_ = [];
      for (let p = 1; p <= pages; p++) {
        if (p === 1 || p === pages || Math.abs(p - cur) <= 1) window_.push(p);
      }
      let prev = 0;
      window_.forEach((p) => {
        if (prev && p - prev > 1) gap();
        btn(String(p), p, { current: p === cur, label: 'Page ' + p });
        prev = p;
      });

      btn('›', cur + 1, { disabled: cur === pages, label: 'Next page' });
    }

    goTo(page) {
      this.page = Math.min(Math.max(1, page), this.pageCount);
      this.render();
      if (this.scrollOnChange && this.grid) {
        const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
        const top = this.grid.getBoundingClientRect().top + window.pageYOffset - header - 24;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    }
  }

  customElements.define('reviews-grid', ReviewsGrid);
}
